import React, { useState, useMemo } from 'react';
import './ComplianceFiling.css';

const MOCK_FILINGS = [
  { id: 1, state: 'CA', filingNumber: 'SL-CA-2026-001', policy: 'CY-2026-001', premium: 48500, taxRate: 3.5, taxDue: 1697.5, status: 'filed', deadline: '2026-02-28' },
  { id: 2, state: 'NY', filingNumber: 'SL-NY-2026-002', policy: 'CY-2026-002', premium: 31200, taxRate: 3.0, taxDue: 936, status: 'pending', deadline: '2026-03-05' },
  { id: 3, state: 'TX', filingNumber: 'SL-TX-2026-003', policy: 'CY-2025-089', premium: 67500, taxRate: 4.85, taxDue: 3273.75, status: 'filed', deadline: '2026-03-15' },
  { id: 4, state: 'FL', filingNumber: 'SL-FL-2026-004', policy: 'CY-2026-003', premium: 89000, taxRate: 5.0, taxDue: 4450, status: 'overdue', deadline: '2026-02-20' },
  { id: 5, state: 'IL', filingNumber: 'SL-IL-2026-005', policy: 'CY-2026-004', premium: 52000, taxRate: 3.0, taxDue: 1560, status: 'draft', deadline: '2026-03-20' },
  { id: 6, state: 'PA', filingNumber: 'SL-PA-2026-006', policy: 'CY-2026-005', premium: 18900, taxRate: 3.0, taxDue: 567, status: 'pending', deadline: '2026-03-25' },
  { id: 7, state: 'NJ', filingNumber: 'SL-NJ-2026-007', policy: 'CY-2026-006', premium: 35600, taxRate: 3.0, taxDue: 1068, status: 'filed', deadline: '2026-04-01' },
  { id: 8, state: 'GA', filingNumber: 'SL-GA-2026-008', policy: 'CY-2025-082', premium: 42000, taxRate: 4.0, taxDue: 1680, status: 'overdue', deadline: '2026-02-18' },
  { id: 9, state: 'OH', filingNumber: 'SL-OH-2026-009', policy: 'CY-2026-001', premium: 48500, taxRate: 4.0, taxDue: 1940, status: 'pending', deadline: '2026-03-10' },
  { id: 10, state: 'MA', filingNumber: 'SL-MA-2026-010', policy: 'CY-2026-003', premium: 89000, taxRate: 4.0, taxDue: 3560, status: 'draft', deadline: '2026-04-05' },
];

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDaysUntilDeadline(deadline) {
  if (!deadline) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(deadline);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

function getStatusBadgeClass(status) {
  switch (status?.toLowerCase()) {
    case 'filed': return 'cof__badge cof__badge--filed';
    case 'pending': return 'cof__badge cof__badge--pending';
    case 'overdue': return 'cof__badge cof__badge--overdue';
    case 'draft': return 'cof__badge cof__badge--draft';
    default: return 'cof__badge';
  }
}

function getDeadlineClass(days) {
  if (days == null) return '';
  if (days < 0) return 'cof__deadline--overdue';
  if (days <= 7) return 'cof__deadline--urgent';
  if (days <= 14) return 'cof__deadline--soon';
  return '';
}

export function ComplianceFiling() {
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredFilings = useMemo(() => {
    if (statusFilter === 'all') return MOCK_FILINGS;
    return MOCK_FILINGS.filter((f) => (f.status || '').toLowerCase() === statusFilter);
  }, [statusFilter]);

  const stats = useMemo(() => {
    const total = MOCK_FILINGS.length;
    const filed = MOCK_FILINGS.filter((f) => f.status === 'filed').length;
    const pending = MOCK_FILINGS.filter((f) => f.status === 'pending').length;
    const overdue = MOCK_FILINGS.filter((f) => f.status === 'overdue').length;
    return { total, filed, pending, overdue };
  }, []);

  return (
    <div className="cof">
      <h1 className="cof__title">Surplus Lines Compliance Filing</h1>

      {/* Stat cards */}
      <div className="cof__stats">
        <div className="cof__stat-card card">
          <span className="cof__stat-label">Total Filings</span>
          <span className="cof__stat-value">{stats.total}</span>
        </div>
        <div className="cof__stat-card card">
          <span className="cof__stat-label">Filed</span>
          <span className="cof__stat-value cof__stat-value--green">{stats.filed}</span>
        </div>
        <div className="cof__stat-card card">
          <span className="cof__stat-label">Pending</span>
          <span className="cof__stat-value cof__stat-value--yellow">{stats.pending}</span>
        </div>
        <div className="cof__stat-card card">
          <span className="cof__stat-label">Overdue</span>
          <span className="cof__stat-value cof__stat-value--red">{stats.overdue}</span>
        </div>
      </div>

      {/* Filter */}
      <div className="cof__filters">
        <select
          className="cof__filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="filed">Filed</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {/* Table */}
      <div className="cof__section">
        <h2 className="cof__section-title">Surplus Lines Filings</h2>
        <div className="cof__table-wrap card">
          <table className="cof__table">
            <thead>
              <tr>
                <th>State</th>
                <th>Filing #</th>
                <th>Policy</th>
                <th>Premium</th>
                <th>Tax Rate</th>
                <th>Tax Due</th>
                <th>Status</th>
                <th>Deadline</th>
              </tr>
            </thead>
            <tbody>
              {filteredFilings.length > 0 ? (
                filteredFilings.map((f) => {
                  const days = getDaysUntilDeadline(f.deadline);
                  return (
                    <tr key={f.id} className="cof__table-row">
                      <td className="cof__table-state">{f.state}</td>
                      <td className="cof__table-filing">{f.filingNumber}</td>
                      <td className="cof__table-policy">{f.policy}</td>
                      <td className="cof__table-currency">{formatCurrency(f.premium)}</td>
                      <td className="cof__table-rate">{f.taxRate}%</td>
                      <td className="cof__table-currency">{formatCurrency(f.taxDue)}</td>
                      <td>
                        <span className={getStatusBadgeClass(f.status)}>{f.status}</span>
                      </td>
                      <td>
                        <span className={`cof__deadline ${getDeadlineClass(days)}`}>
                          {formatDate(f.deadline)}
                          {days != null && (
                            <span className="cof__deadline-days">
                              {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="cof__table-empty">
                    No filings match your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
